<template>
  <div>
    <section class="bg-primary-700 pt-175 pb-75 md:pb-100 lg:pb-125">
      <div class="container">
        <div class="max-w-screen-xl mx-auto">
          <div class="grid md:grid-cols-2">
            <div class="md:-ml-150 md:translate-x-150 md:mt-100">
              <h1
                v-if="data?.entry?.heading"
                class="wysiwyg-title text-4xl lg:text-5xl xl:text-6xl text-primary-300"
                v-html="data.entry.heading"
              />
              <ul
                v-if="data?.entry?.uspList.length"
                class="text-primary-300 mt-30"
              >
                <li
                  v-for="item in data.entry.uspList"
                  :key="`usp-item-${item.id}`"
                  class="text-lg md:text-[23px] inline-flex items-baseline gap-x-[8px]"
                >
                  <CheckmarkIcon
                    width="15"
                    class="shrink-0"
                  />
                  <span v-html="item.usp" />
                </li>
              </ul>

              <NuxtLink
                v-if="data?.entry?.introLink.url"
                :to="data.entry.introLink.url"
                :target="data.entry.introLink.target"
                :aria-label="data.entry.introLink.arialabel"
                class="my-15 transition-colors bg-primary-300 hover:bg-primary-500 text-white font-bold text-base xl:text-lg px-20 py-15 xl:px-25 items-center flex lowercase font-serif sm:w-fit justify-center"
              >
                {{ data.entry.introLink.text }}
              </NuxtLink>
            </div>
            <div class="md:row-span-3">
              <img
                v-if="data?.entry?.image.length"
                :src="data.entry.image[0].url"
                :srcset="data.entry.image[0].srcset"
                sizes="(max-width: 600px) 345px, (max-width: 1024px) 497px, 100vw"
                :alt="data.entry.image.title"
                width="1280"
                height="1280"
                class="w-full md:mt-0"
              >
            </div>
          </div>
        </div>

        <div class="grid md:grid-cols-12 gap-30 md:gap-0 text-white pt-75 md:pt-100">
          <div class="md:col-span-4 md:pr-30 md:pt-30 relative z-10 before:right-0 before:inset-y-0 before:w-screen before:absolute before:bg-primary-700 before:-z-10">
            <h2
              v-if="data?.entry?.processTitle"
              class="wysiwyg-title text-4xl xl:text-5xl mb-15"
              v-html="data.entry.processTitle"
            />
            <div
              v-if="data?.entry?.processText"
              class="wysiwyg text-base lg:text-[23px] mb-15"
              v-html="data.entry.processText"
            />
            <div
              v-if="data?.entry?.processAuthor"
              class="flex gap-x-20 items-center"
            >
              <div class="w-60 h-60 rounded-full overflow-hidden bg-primary-500">
                <img
                  v-if="data.entry.processAuthorAvatar?.length"
                  :src="data.entry.processAuthorAvatar[0].url"
                  :alt="data.entry.processAuthor"
                  class="w-full h-full object-cover"
                  width="60"
                  height="60"
                >
              </div>
              <span>{{ data.entry.processAuthor }}</span>
            </div>
          </div>
          <div
            v-if="data?.entry?.processSteps.length"
            class="md:col-span-8"
          >
            <Swiper
              slides-per-view="auto"
              :modules="[SwiperPagination]"
              :pagination="{
                el: paginationEl,
                type: 'bullets',
                bulletClass: 'swiper-bullet-custom',
                bulletActiveClass: 'is-active',
                clickable: true,
              }"
              grab-cursor
              class="[&.swiper]:overflow-visible"
            >
              <SwiperSlide
                v-for="(item, key) in data.entry.processSteps"
                :key="`process-step-${item.id}`"
                class="aspect-[50/35] md:[&.swiper-slide]:!w-2/3 mr-15 last:mr-0 relative"
              >
                <img
                  v-if="item.image.length"
                  :src="item.image[0].url"
                  :alt="item.heading"
                  :loading="key > 1 ? 'lazy' : null"
                  fetchpriority="low"
                  class="absolute w-full h-full top-0 left-0 object-cover"
                  width="500"
                  height="350"
                >
                <div class="absolute inset-x-0 bottom-0 px-20 pb-20 pt-100 bg-gradient-to-t from-black/50 h-fit">
                  <div class="font-bold text-base lg:text-xl leading-none lowercase mb-5">
                    {{ $t('interior-advice.step', { step: key + 1 }) }}
                  </div>
                  <h4 class="text-xl lg:text-[35px] font-serif lowercase leading-none">
                    {{ item.heading }}
                  </h4>
                </div>
              </SwiperSlide>
              <div
                ref="paginationEl"
                class="absolute !-bottom-20 left-0 flex gap-x-10"
              />
            </Swiper>
          </div>
        </div>
      </div>
    </section>

    <section class="container my-100 lg:my-125 !overflow-visible">
      <div class="grid md:grid-cols-2 gap-30 lg:gap-x-50 max-w-screen-xl mx-auto">
        <div
          v-if="data?.entry?.designImages.length"
          class="relative flex justify-end md:-mt-50"
        >
          <img
            :src="data.entry.designImages[0].url"
            :srcset="data.entry.designImages[0].srcset"
            sizes="60vw"
            :alt="data.entry.designImages[0].title"
            width="1000"
            height="1000"
            loading="lazy"
            class="w-10/12 relative z-10 object-cover"
          >
          <img
            v-if="data.entry.designImages.length > 1"
            :src="data.entry.designImages[1].url"
            :srcset="data.entry.designImages[1].srcset"
            sizes="60vw"
            :alt="data.entry.designImages[1].title"
            width="1000"
            height="1000"
            loading="lazy"
            class="absolute w-2/5 -bottom-50 left-0 z-10 object-cover"
          >
        </div>
        <div class="mt-50 md:mt-0 md:pr-50">
          <h3
            v-if="data?.entry?.designTitle"
            class="wysiwyg-title text-4xl lg:text-5xl xl:text-6xl text-primary-700 mb-15"
            v-html="data.entry.designTitle"
          />
          <div
            v-if="data?.entry?.designText"
            class="wysiwyg mb-25"
            v-html="data.entry.designText"
          />

          <NuxtLink
            v-if="data?.entry?.designLink.url"
            :to="data.entry.designLink.url"
            :target="data.entry.designLink.target"
            :aria-label="data.entry.designLink.arialabel"
            class="transition-colors bg-primary-300 hover:bg-primary-500 text-white font-bold text-base xl:text-lg px-20 py-15 xl:px-25 items-center flex lowercase font-serif sm:w-fit justify-center"
          >
            {{ data.entry.designLink.text }}
          </NuxtLink>
        </div>
      </div>
    </section>

    <ContentBlocks :content-blocks="data?.entry?.contentBlocks" />

    <section class="container my-100 lg:my-125">
      <div class="grid lg:grid-cols-2 gap-30 lg:gap-x-50 max-w-screen-xl mx-auto">
        <div class="lg:pl-50 lg:pt-50">
          <h3
            v-if="data?.entry?.interiorViewersHeading"
            class="wysiwyg-title text-4xl lg:text-5xl xl:text-6xl text-primary-700 mb-15"
            v-html="data.entry.interiorViewersHeading"
          />
          <div
            v-if="data?.entry?.interiorViewersText"
            class="wysiwyg mb-25"
            v-html="data.entry.interiorViewersText"
          />

          <NuxtLink
            v-if="data?.entry?.interiorViewersLink.url"
            :to="data.entry.interiorViewersLink.url"
            :target="data.entry.interiorViewersLink.target"
            :aria-label="data.entry.interiorViewersLink.arialabel"
            class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-15 text-primary-500 transition-colors hover:text-primary-700"
          >
            <span>{{ data.entry.interiorViewersLink.text }}</span>
            <ChevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
          </NuxtLink>
        </div>
        <div v-if="data?.entry?.interiorViewersSlider?.length">
          <Swiper
            slides-per-view="auto"
            :navigation="{
              nextEl,
              prevEl,
              disabledClass: 'is-disabled',
            }"
            :modules="[SwiperNavigation]"
            grab-cursor
          >
            <SwiperSlide
              v-for="item in data.entry.interiorViewersSlider"
              :key="`slide-${item.id}`"
              class="aspect-square [&.swiper-slide]:!w-full relative"
            >
              <figure>
                <img
                  v-if="item.image.length"
                  :src="item.image[0].url"
                  :srcset="item.image[0].srcset"
                  sizes="60vw"
                  :alt="item.heading"
                  width="1000"
                  height="1000"
                  loading="lazy"
                  class="w-full h-full object-cover"
                >
                <figcaption class="hidden lg:block absolute bg-gradient-to-t from-black/30 z-10 inset-x-0 bottom-0 pl-150 pt-100 pb-25 text-white">
                  {{ item.heading }}
                </figcaption>
              </figure>
            </SwiperSlide>
            <div class="absolute inset-x-0 bottom-0 z-10 flex p-15 gap-x-5">
              <button
                ref="prevEl"
                type="button"
                class="p-15 bg-primary-300 text-white [&.is-disabled]:opacity-50 hover:bg-primary-500 transition-colors"
              >
                <ChevronIcon class="w-20 rotate-90 pointer-events-none" />
              </button>
              <button
                ref="nextEl"
                type="button"
                class="p-15 bg-primary-300 text-white [&.is-disabled]:opacity-50 hover:bg-primary-500 transition-colors"
              >
                <ChevronIcon class="w-20 -rotate-90 pointer-events-none" />
              </button>
            </div>
          </Swiper>
        </div>
      </div>
    </section>

    <section class="container my-100 lg:my-125">
      <div class="grid md:grid-cols-2 gap-30 lg:gap-x-50 max-w-screen-xl mx-auto">
        <div class="md:pr-50 md:pt-50 md:col-start-2">
          <h3
            v-if="data?.entry?.adviceTitle"
            class="wysiwyg-title text-4xl lg:text-5xl xl:text-6xl text-primary-500 mb-15"
            v-html="data.entry.adviceTitle"
          />
          <div
            v-if="data?.entry?.adviceText"
            class="wysiwyg mb-25"
            v-html="data.entry.adviceText"
          />

          <NuxtLink
            v-if="data?.entry?.adviceLink.url"
            :to="data.entry.adviceLink.url"
            :target="data.entry.adviceLink.target"
            :aria-label="data.entry.adviceLink.arialabel"
            class="transition-colors bg-primary-300 hover:bg-primary-500 text-white font-bold text-base xl:text-lg px-20 py-15 xl:px-25 items-center flex lowercase font-serif sm:w-fit justify-center"
          >
            {{ data.entry.adviceLink.text }}
          </NuxtLink>
        </div>
        <div class="md:col-start-1 md:row-start-1">
          <img
            :src="data?.entry.adviceImage[0].url"
            :srcset="data.entry.adviceImage[0].srcset"
            sizes="60vw"
            :alt="data.entry.adviceImage[0].title"
            loading="lazy"
            width="600"
            height="745"
            class="w-full object-cover"
          >
        </div>
      </div>
    </section>

    <Reviews />

    <section
      id="afspraak"
      class="py-75 md:py-100 lg:py-125 bg-primary-700 text-white scroll-mt-75 md:scroll-mt-150"
    >
      <div class="container">
        <div class="max-w-screen-xl mx-auto">
          <div class="grid lg:grid-cols-2 gap-30">
            <div class="lg:max-w-md lg:pt-50">
              <h2
                v-if="data?.entry?.appointmentTitle"
                class="wysiwyg-title text-4xl xl:text-5xl mb-15"
                v-html="data.entry.appointmentTitle"
              />
              <div
                v-if="data?.entry?.appointmentText"
                class="wysiwyg text-base lg:text-[23px] mb-15"
                v-html="data.entry.processText"
              />
              <div
                v-if="data?.entry?.appointmentPerson"
                class="flex gap-x-20 items-center"
              >
                <div class="w-60 h-60 rounded-full overflow-hidden bg-primary-500">
                  <img
                    v-if="data?.entry?.appointmentPersonAvatar.length"
                    :src="data.entry.appointmentPersonAvatar[0].url"
                    :alt="data.entry.appointmentPerson"
                    class="w-full h-full object-cover"
                    width="60"
                    height="60"
                  >
                </div>
                <span>{{ data.entry.appointmentPerson }}</span>
              </div>
            </div>
            <div>
              <Message
                :message="apiMessage"
                class="mb-30"
              />

              <form @submit.prevent="sendForm">
                <input
                  type="hidden"
                  name="action"
                  value="contact-form/send"
                />
                <input
                  type="hidden"
                  name="subject"
                  value="Interieuradvies aanvraag"
                />
                <p class="text-xl font-bold">
                  {{ $t('interior-advice.your-data') }}
                </p>
                <div class="relative mt-20">
                  <input
                    id="name"
                    v-model="form.fromName"
                    type="text"
                    class="form-input form-input-dark"
                    name="fromName"
                    :placeholder="`${$t('interior-advice.name')} *`"
                    required
                  >
                  <label
                    for="name"
                    class="form-label"
                  >{{ $t('interior-advice.name') }} *</label>
                </div>
                <div class="relative mt-20">
                  <input
                    id="phone"
                    v-model="form.phone"
                    type="tel"
                    class="form-input form-input-dark"
                    name="message[Telefoonnummer]"
                    :placeholder="`${$t('interior-advice.phone')} *`"
                    required
                  >
                  <label
                    for="phone"
                    class="form-label"
                  >{{ $t('interior-advice.phone') }} *</label>
                </div>
                <div class="relative mt-20">
                  <input
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="form-input form-input-dark"
                    name="fromEmail"
                    :placeholder="`${$t('interior-advice.email')} *`"
                    required
                  >
                  <label
                    for="email"
                    class="form-label"
                  >{{ $t('interior-advice.email') }} *</label>
                </div>
                <div class="relative mt-20">
                  <input
                    id="rooms"
                    type="text"
                    class="form-input form-input-dark"
                    name="message[Type ruimte]"
                    :placeholder="$t('interior-advice.rooms')"
                    required
                  >
                  <label
                    for="rooms"
                    class="form-label"
                  >{{ $t('interior-advice.rooms') }}</label>
                </div>
                <div class="mt-40">
                  <label class="group flex font-bold cursor-pointer hover:underline justify-between items-center space-x-20">
                    {{ $t('interior-advice.upload-images') }}
                    <span class="flex flex-shrink-0 w-50 h-50 items-center justify-center border border-secondary rounded-full group-hover:bg-secondary group-hover:text-white transition">
                      <UploadIcon />
                    </span>
                    <input
                      type="file"
                      name="attachment[]"
                      accept="image/*"
                      class="hidden"
                      multiple
                      @change="addPreview"
                    >
                  </label>
                  <div class="flex flex-wrap text-primary-700 text-sm">
                    <div
                      v-for="(preview, i) in previews"
                      :key="`preview-${i}`"
                      class="mt-30 mr-20"
                    >
                      <img
                        :src="preview"
                        alt=""
                        class="h-[130px]"
                      >
                    </div>
                  </div>
                </div>

                <p class="text-sm text-primary-300 mt-25">
                  Na ontvangst van je contactaanvraag nemen we op werkdagen nog dezelfde dag telefonisch contact met je op.<br><br>
                  <i>* Verplichte velden</i>
                </p>

                <button
                  type="submit"
                  :class="[loading ? 'bg-primary-700' : 'bg-primary-300']"
                  class="flex w-full text-xl lg:text-2xl px-30 py-20 mt-50 items-center justify-center space-x-10 text-white font-serif font-bold hover:bg-primary-500 transition"
                >
                  <img
                    v-if="loading"
                    src="@/assets/img/icon-loader.png"
                    alt="loading..."
                    class="w-[30px] h-[30px] animate-spin"
                  >
                  {{ loading ? $t('interior-advice.sending') : $t('interior-advice.send') }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="data?.entry?.faqItems?.length"
      class="container my-75 lg:my-100"
    >
      <div class="max-w-4xl mx-auto">
        <details
          v-for="item in data.entry.faqItems"
          :key="`faq-item-${item.id}`"
          class="border-b border-primary-200"
        >
          <summary class="[details[open]_&]:text-primary-500 py-15 transition-colors cursor-pointer text-xl lg:text-2xl font-serif text-primary-700 font-bold flex items-center gap-x-10 justify-between">
            {{ item.question }}
            <PlusIcon
              width="18"
              height="18"
              class="[details[open]_&]:hidden block"
            />
            <span class="h-2 bg-current w-[18px] [details[open]_&]:block hidden" />
          </summary>
          <div
            v-if="item.answer"
            class="text-primary-500 wysiwyg pb-20"
            v-html="item.answer"
          />
        </details>
      </div>
    </section>
  </div>
</template>

<script setup>
import {
  ref,
  $fetch,
  useI18n,
  useAsyncGql,
  useNuxtApp,
  useSeo,
  watch,
  SwiperNavigation,
  SwiperPagination,
} from '#imports';

// Assets
import CheckmarkIcon from '@/assets/img/icon-checkmark.svg';
import ChevronIcon from '@/assets/img/icon-chevron.svg';
import UploadIcon from '@/assets/img/icon-upload.svg';
import PlusIcon from '@/assets/img/icon-plus.svg';

// Data
const { locale, t } = useI18n({ useScope: 'global' });
const { data } = await useAsyncGql('interiorAdvice', {
  site: locale.value,
});
const { $gtm } = useNuxtApp();

// Elements
const prevEl = ref('');
const nextEl = ref('');
const paginationEl = ref('');

// States
const apiMessage = ref(null);
const loading = ref(null);
const previews = ref([]);
const form = ref({
  fromName: '',
  phone: '',
  email: '',
});

// Watcher
watch(data, (value) => {
  if (value?.entry?.seomatic) {
    useSeo(value.entry.seomatic);
  }
}, { immediate: true });

// Methods
/**
 * Adds preview images
 *
 * @param {Event} e
 */
const addPreview = (e) => {
  previews.value = [];
  Array.from(e.target.files).forEach((file) => {
    previews.value.push(URL.createObjectURL(file));
  });
};

/**
 * Sends contact form data
 *
 * @param {Event} e
 */
const sendForm = async (e) => {
  apiMessage.value = null;
  const body = new FormData(e.target);
  loading.value = true;

  try {
    await $fetch('/', {
      method: 'POST',
      body,
    });

    previews.value = [];
    e.target.reset();

    apiMessage.value = {
      error: false,
      message: t('contact.success'),
    };

    // GTM tracking
    $gtm.trackEvent({
      event: 'interieuradvies',
      ecommerce: {
        customer_name: form.value.fromName,
        customer_mail: form.value.email,
        customer_phone: form.value.phone,
      },
    });
  } catch (error) {
    apiMessage.value = {
      error: true,
      ...error.data,
    };
  } finally {
    loading.value = false;
  }
};
</script>
